import type {
  MiddlewareHealthReport,
  MiddlewareHealthSource,
  MiddlewareJobState,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Activity } from 'lucide-react';
import { MiddlewareHealthCard, formatDuration } from './MiddlewareHealthCard';

interface MiddlewareHealthPanelProps {
  /** One report per middleware of the selected customer (absent when none). */
  reports: MiddlewareHealthReport[] | undefined;
}

const STATE_LABEL: Record<MiddlewareJobState, string> = {
  ok: 'On schedule',
  skipped: 'Skipped',
  partial: 'Partly failed',
  failed: 'Failed',
  late: 'Late',
  missed: 'Missed',
  inactive: 'Not scheduled',
};

const STATE_CLASS: Record<MiddlewareJobState, string> = {
  ok: 'border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200',
  skipped: 'border-green-300 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-200',
  partial: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200',
  late: 'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200',
  failed: 'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
  missed: 'border-red-300 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200',
  inactive: 'border-slate-300 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300',
};

const OVERALL_TEXT: Record<'green' | 'yellow' | 'red', { text: string; className: string }> = {
  green: { text: 'Healthy', className: STATE_CLASS.ok },
  yellow: { text: 'Needs attention', className: STATE_CLASS.late },
  red: { text: 'Failing', className: STATE_CLASS.failed },
};

/** Seconds from now until `iso` (negative once it has passed). */
function secondsUntil(iso: string): number {
  return Math.round((new Date(iso).getTime() - Date.now()) / 1000);
}

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  return `${formatDuration(Math.max(0, -secondsUntil(iso)))} ago`;
}

function nextRunText(job: MiddlewareHealthSource): string {
  if (!job.next_run_at) return '—';
  const s = secondsUntil(job.next_run_at);
  return s >= 0 ? `in ${formatDuration(s)}` : `due ${formatDuration(-s)} ago`;
}

/**
 * Middleware health on the Import Monitor, one block per middleware.
 *
 * - Never sent a heartbeat (`unknown`): one muted line with the last time any
 *   of its tokens called in — not an alarm, it may simply predate heartbeats.
 * - Reports its schedule (`mode: schedule`): a job table, each job judged
 *   against the next run it announced, plus the Issues feed.
 * - Otherwise (PNA): the existing silent-alert + Issues card.
 *
 * @param reports - the `middleware_health` list from the middleware-status endpoint
 * @link MiddlewareHealthCard for the Issues feed
 */
export function MiddlewareHealthPanel({ reports }: MiddlewareHealthPanelProps) {
  if (!reports || reports.length === 0) return null;
  return (
    <>
      {reports.map((report) => (
        <ReportBlock key={`${report.customer_id}-${report.partner}`} report={report} />
      ))}
    </>
  );
}

function ReportBlock({ report }: { report: MiddlewareHealthReport }) {
  if (report.overall === 'unknown') {
    return (
      <div className="mb-4 space-y-2">
        <div className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm text-muted-foreground">
          <Activity className="h-4 w-4 shrink-0" />
          <span>
            {report.partner_label}: no heartbeat received yet · last contact {ago(report.last_contact_at)}
          </span>
        </div>
        <MiddlewareHealthCard health={report} label={report.partner_label} silenceAlert={false} />
      </div>
    );
  }
  if (report.mode !== 'schedule') {
    return <MiddlewareHealthCard health={report} label={report.partner_label} />;
  }
  return (
    <div className="mb-4 space-y-2">
      <ScheduledJobsCard report={report} />
      <MiddlewareHealthCard health={report} label={report.partner_label} silenceAlert={false} />
    </div>
  );
}

function ScheduledJobsCard({ report }: { report: MiddlewareHealthReport }) {
  const { formatDateTime } = useFormatters();
  const overall = OVERALL_TEXT[report.overall === 'unknown' ? 'red' : report.overall];
  const jobs = report.sources.filter((s) => s.state);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm">
          <Activity className="h-4 w-4" />
          {report.partner_label}
          <Badge variant="outline" className={overall.className}>{overall.text}</Badge>
          {report.version && (
            <span className="ml-auto font-mono text-xs font-normal text-muted-foreground">
              Build {report.version}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Job</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last run</TableHead>
              <TableHead>Next run</TableHead>
              <TableHead className="text-right">Records</TableHead>
              <TableHead className="text-right">Took</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => {
              const state = job.state as MiddlewareJobState;
              return (
                <TableRow key={job.source}>
                  <TableCell className="font-mono text-xs">
                    {job.source}
                    {job.last_error_message && (state === 'failed' || state === 'partial') && (
                      <div className="mt-0.5 max-w-md whitespace-normal font-sans text-xs text-muted-foreground">
                        {job.last_error_message}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATE_CLASS[state]}>{STATE_LABEL[state]}</Badge>
                  </TableCell>
                  <TableCell className="text-sm" title={job.last_seen_at ? formatDateTime(job.last_seen_at) : ''}>
                    {ago(job.last_seen_at)}
                  </TableCell>
                  <TableCell className="text-sm" title={job.next_run_at ? formatDateTime(job.next_run_at) : ''}>
                    {nextRunText(job)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{job.last_records ?? '—'}</TableCell>
                  <TableCell className="text-right tabular-nums">
                    {job.last_duration_ms === null ? '—' : formatDuration(Math.round(job.last_duration_ms / 1000))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
